import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';
import { walletApi } from '../api';

// A faux payment gateway for testing. Looks like a real checkout, credits the
// wallet via the sandbox endpoint (backend FAUX_PAYMENTS must be on). No real
// money moves; the card fields are pure theatre.
const SandboxPay: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const reference = params.get('ref') || '';
  const amount = params.get('amount') || '0.00';   // total charged
  const credit = params.get('credit') || amount;   // lands in wallet

  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const pay = async () => {
    setError('');
    setProcessing(true);
    try {
      await walletApi.completeFauxDeposit(reference);
      setDone(true);
      setTimeout(() => navigate('/deposit/return'), 1200);
    } catch (e: any) {
      setProcessing(false);
      setError(e?.response?.data?.detail || 'Payment failed (impressively, even the fake one).');
    }
  };

  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Fake gateway header */}
        <div className="bg-gradient-to-r from-brand-800 to-brand-950 text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <ShieldCheck className="h-5 w-5" />
            <span className="font-extrabold tracking-tight">PayFaux</span>
            <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded uppercase font-bold">Sandbox</span>
          </div>
          <span className="text-xs text-green-100">Secure-ish Checkout</span>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
            <p className="text-lg font-bold text-gray-800">Payment approved!</p>
            <p className="text-sm text-gray-500 mt-1">Your imaginary money is now real money in your wallet. Taking you back…</p>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="text-center">
              <p className="text-xs text-gray-500">Fairplay Wallet Top-up</p>
              <p className="text-3xl font-extrabold text-gray-900">R{amount}</p>
              <p className="text-[11px] text-gray-500 mt-1">R{credit} to your wallet · the rest is fees</p>
              <p className="text-[11px] text-gray-400">Ref {reference || '—'}</p>
            </div>

            {/* Theatrical card form (does nothing) */}
            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 mb-1">Card number</label>
                <div className="flex items-center border border-gray-200 rounded-lg px-3 py-2.5 bg-gray-50">
                  <input
                    disabled
                    value="4242 4242 4242 4242"
                    className="flex-1 bg-transparent text-gray-700 tracking-widest text-sm outline-none"
                  />
                  <span className="text-xs font-bold text-blue-600">VISA*</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1">Expiry</label>
                  <input disabled value="07/49" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 bg-gray-50 text-gray-700 text-sm outline-none" />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-gray-500 mb-1">CVV</label>
                  <input disabled value="•••" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 bg-gray-50 text-gray-700 text-sm outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-500 mb-1">Cardholder</label>
                <input disabled value="A. VERY GOOD GOLFER" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 bg-gray-50 text-gray-700 text-sm outline-none" />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 text-center">{error}</div>
            )}

            <button
              onClick={pay}
              disabled={processing || !reference}
              className="w-full bg-gradient-to-r from-emerald-600 to-brand-700 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center space-x-2 hover:from-emerald-500 hover:to-brand-600 transition active:scale-[0.98] disabled:opacity-60"
            >
              {processing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-4 w-4" />}
              <span>{processing ? 'Definitely charging your card…' : `Pay R${amount}`}</span>
            </button>

            <button
              onClick={() => navigate('/deposit/cancel')}
              disabled={processing}
              className="w-full text-sm text-gray-500 hover:text-gray-700 py-1"
            >
              Cancel and flee
            </button>

            <p className="text-[10px] text-gray-400 text-center leading-relaxed">
              🔒 Not a real payment gateway. No card is charged, no money moves — this is a
              sandbox for testing. Your "winnings" are equally fictional until you go live.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SandboxPay;
